import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/modules/userSlice";
import { DownloadFormHtmlToPdf, SelfDeclarationAPI } from "../api";
import useDraft from "../../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../../config/joineeFormConstants";
import JoineeFormShell, {
  JoineeFormFooterActions,
} from "../NewJoineForm/JoineeFormShell";
import {
  FormSectionTitle,
  FormTextField,
  FormDateField,
  FormTwoCol,
  FormCheckboxField,
} from "../NewJoineForm/JoineeFormFields";
import ApprovedCustomizedDialogs from "../Model/ModelApproved";
import {
  canShowHrApprove,
  canShowHrResubmit,
  canShowHrReviewActions,
  isJoineeEmployeeFormLocked,
} from "../../config/joineeConstants";
import ReSubmitCustomizedDialogs from "../Model/ModelReSubmit";
import LoadingButtons from "../Model/LoadingButtonModel";
import HrResubmitNotice from "../Joinee/HrResubmitNotice";

import { ruleSchema, validateDraft, notifyFormInvalid } from "../../utils/inputRules";

var EMPTY_DISPLAY = "—";

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function displayValue(value) {
  if (value === null || value === undefined) {
    return EMPTY_DISPLAY;
  }
  var text = String(value).trim();
  return text ? text : EMPTY_DISPLAY;
}

function formatPeriodOfStay(value) {
  if (value === null || value === undefined || value === "") {
    return EMPTY_DISPLAY;
  }
  var text = String(value).trim();
  if (!text) {
    return EMPTY_DISPLAY;
  }
  if (/year/i.test(text) || /month/i.test(text) || /day/i.test(text)) {
    return text;
  }
  return text + " Year(s)";
}

function formatDob(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : displayValue(value);
}

function safeFatherName(userResp) {
  if (!userResp || !userResp.father) {
    return "";
  }
  if (typeof userResp.father === "string") {
    return userResp.father;
  }
  return userResp.father.name || "";
}

function mapAddress(addr) {
  var a = addr || {};
  return {
    addressLine1: a.addressLine1 || "",
    addressLine2: a.addressLine2 || "",
    landmark: a.landmark || "",
    postalCode: a.postalCode || "",
    periodOfStay: a.periodOfStay || "",
    natureOfResidency: a.natureOfResidency || "",
  };
}

function mapEmployment(row) {
  if (!row) {
    return null;
  }
  var line2 = row.addressLine2 ? ", " + row.addressLine2 : "";
  return {
    companyName: row.employerName || "",
    address: (row.addressLine1 || "") + line2,
    companyID: row.employeeCode || "",
    period: row.periodOfEmployment || "",
    cityCode: row.city || "",
    designation: row.designation || "",
    state: row.state || "",
    remuneration: row.remuneration || "",
    contact: row.companyContact || "",
    reported: row.reportedTo || "",
    reportedDesignation: row.reportedPersonDesig || "",
    reasonLeaving: row.reasonForLeaving || "",
    referenceContactPerson1Name: row.referenceContactPerson1Name || "",
    referenceContactPerson1Contact: row.referenceContactPerson1Contact || "",
    referenceContactPerson2Name: row.referenceContactPerson2Name || "",
    referenceContactPerson2Contact: row.referenceContactPerson2Contact || "",
  };
}

function collectReferences(employments) {
  var refs = [];
  (employments || []).forEach(function (emp) {
    if (!emp) {
      return;
    }
    if (emp.referenceContactPerson1Name || emp.referenceContactPerson1Contact) {
      refs.push({
        name: emp.referenceContactPerson1Name || "",
        phone: emp.referenceContactPerson1Contact || "",
      });
    }
    if (emp.referenceContactPerson2Name || emp.referenceContactPerson2Contact) {
      refs.push({
        name: emp.referenceContactPerson2Name || "",
        phone: emp.referenceContactPerson2Contact || "",
      });
    }
  });
  while (refs.length < 2) {
    refs.push({ name: "", phone: "" });
  }
  return refs.slice(0, 2);
}

function yearFromDate(value) {
  if (!value) {
    return "";
  }
  var d = dayjs(value);
  if (d.isValid()) {
    return String(d.year());
  }
  var parts = String(value).split("-");
  return parts[0] || "";
}

function ReadOnlyRow(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "16px", sm: "32px" },
        width: "100%",
        mb: 2,
      }}
    >
      {(props.items || []).map(function (item, index) {
        return (
          <Box
            key={item.label + "-" + index}
            sx={{
              display: "flex",
              flex: "1 1 0",
              gap: "16px",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#848F9A",
                lineHeight: 1.14,
                width: { xs: "140px", sm: "164px" },
                flexShrink: 0,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#171A1C",
                lineHeight: 1.14,
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              {item.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function buildValidationSchema() {
  return Yup.object({
    place: ruleSchema("place", { label: "Place", required: true }),
    date: Yup.mixed().nullable().required("Today's date is required"),
    sdfLegalConsent: Yup.boolean()
      .oneOf(
        [true],
        "Please confirm that the information provided is true and accurate"
      ),
  });
}

function Sdf(props) {
  var navigate = useNavigate();
  var params = useParams();
  var routeId = params.id ? params.id : "";
  var redux = useSelector(selectUser);
  var user = redux && redux.user ? redux.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? props.id || routeId : user._id;

  var draftApi = useDraft("selfDeclaration", "main");
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState(false);
  var [formKey, setFormKey] = useState(0);
  var [initialValues, setInitialValues] = useState(null);
  var [sdfFlagButton, setSdfFlagButton] = useState("");
  var [approvedComment, setApprovedComment] = useState("");
  var [reSubmitComment, setReSubmitComment] = useState("");
  var [previewOpen, setPreviewOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState("");
  var [previewLoading, setPreviewLoading] = useState(false);

  useEffect(
    function () {
      var isActive = true;
      if (!userId) {
        setLoading(false);
        return function () {
          isActive = false;
        };
      }

      setLoading(true);
      SelfDeclarationAPI.getSelfDeclaration(userId)
        .then(function (res) {
          if (!isActive) {
            return;
          }
          var data = res && res.data ? res.data : {};
          var userResp = data.userResp || {};
          var edu = data.educationalResp || {};
          var employmentList =
            data.employments && data.employments.length
              ? data.employments
              : data.employment
              ? [data.employment]
              : [];
          var mappedJobs = employmentList
            .map(mapEmployment)
            .filter(Boolean);
          var refs = collectReferences(mappedJobs);
          var sdfdata = data.sdfdata || {};

          var obj = {
            sdfId: sdfdata._id || "",
            candidate_details: {
              name: userResp.name || "",
              fatherName: safeFatherName(userResp),
              dob: edu.userDOB || userResp.dateOfBirth || "",
              phoneNumber: userResp.phoneNumber
                ? String(userResp.phoneNumber)
                : "",
            },
            maritalStatus: userResp.maritalStatus || "",
            gender: userResp.gender || "",
            currentAddress: mapAddress(userResp.currentAddress),
            permanentAddress: mapAddress(userResp.permanentAddress),
            references: refs,
            college_details: {
              name: edu.collegeName || "",
              registrationNumber: edu.registrationNo || "",
              UniversityName: edu.university || "",
              CourseDuration: edu.courseDuration || "",
              yearPassing: yearFromDate(edu.toDate),
              result: edu.result || "",
            },
            employments: mappedJobs,
            Emp_details: mappedJobs[0] || {
              companyName: "",
              address: "",
              companyID: "",
              period: "",
              cityCode: "",
              designation: "",
              state: "",
              remuneration: "",
              contact: "",
              reported: "",
              reportedDesignation: "",
              reasonLeaving: "",
            },
            name: userResp.name || "",
            joiningCompanyName:
              userResp.companyName || "Spanidea Digital Pvt. Ltd.",
            place: sdfdata.place || "",
            date: sdfdata.date || new Date().toISOString(),
            sdfLegalConsent: Boolean(sdfdata.sdfLegalConsent),
            sdfLegalConsentAt: sdfdata.sdfLegalConsentAt || null,
          };

          setApprovedComment(
            userResp.internal_notes &&
              userResp.internal_notes.selfDecalrationFormComment
              ? userResp.internal_notes.selfDecalrationFormComment
              : ""
          );
          setReSubmitComment(
            userResp.selfDecalrationFormComment
              ? userResp.selfDecalrationFormComment
              : ""
          );
          setSdfFlagButton(userResp.isSelfDecalrationFormComplete || "");

          function finish(values) {
            setInitialValues(values);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
          }

          if (!isAdmin) {
            return loadDraft()
              .then(function (loaded) {
                if (!isActive) {
                  return;
                }
                var draftData =
                  loaded && loaded.data ? loaded.data : loaded;
                var applied = applySectionDraftData(
                  "selfDeclaration",
                  "main",
                  obj,
                  draftData
                );
                finish(applied || obj);
              })
              .catch(function () {
                if (isActive) {
                  finish(obj);
                }
              });
          }
          finish(obj);
          return null;
        })
        .catch(function (err) {
          console.log(err);
          if (isActive) {
            setLoading(false);
            toast.error("Unable to load Self Declaration details");
          }
        });

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  function payloadFromValues(values) {
    var accepted = Boolean(values.sdfLegalConsent);
    return {
      place: String(values.place || "").trim(),
      date: values.date || null,
      sdfLegalConsent: accepted,
      sdfLegalConsentAt: accepted
        ? values.sdfLegalConsentAt || new Date().toISOString()
        : null,
    };
  }

  function persistSdf(values) {
    var payload = payloadFromValues(values);
    if (values.sdfId) {
      return SelfDeclarationAPI.updateSelfDeclaration(userId, payload).then(
        function (res) {
          return { res: res, sdfId: values.sdfId };
        }
      );
    }
    return SelfDeclarationAPI.createSelfDeclaration(userId, payload).then(
      function (res) {
        var newId =
          res && res.data && res.data._id ? res.data._id : values.sdfId;
        return { res: res, sdfId: newId };
      }
    );
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(formik, buildValidationSchema()).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var values = formik.values;
    var wrapped = pickSectionDraftData("selfDeclaration", "main", {
      place: values.place,
      date: values.date,
      sdfLegalConsent: Boolean(values.sdfLegalConsent),
      sdfLegalConsentAt: values.sdfLegalConsentAt || null,
      name: values.name,
    });
    setBusy("draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    if (isAdmin) {
      if (formikHelpers && formikHelpers.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }
    setBusy("next");
    persistSdf(values)
      .then(function (result) {
        if (result && result.sdfId && !values.sdfId) {
          formikHelpers.setFieldValue("sdfId", result.sdfId);
        }
        if (
          values.sdfLegalConsent &&
          !values.sdfLegalConsentAt &&
          formikHelpers.setFieldValue
        ) {
          formikHelpers.setFieldValue(
            "sdfLegalConsentAt",
            payloadFromValues(values).sdfLegalConsentAt
          );
        }
        return deleteDraft().then(function () {
          return SelfDeclarationAPI.selfDeclarationStatus(userId);
        });
      })
      .then(function () {
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        setSdfFlagButton("submitted");
        toast.success("Self Declaration submitted");
        navigate("/");
      })
      .catch(function (err) {
        console.log(err);
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save Self Declaration");
      });
  }

  function openPreview() {
    if (!userId) {
      return;
    }
    setPreviewLoading(true);
    DownloadFormHtmlToPdf.getform(userId, "SdfFormHtmlData")
      .then(function (res) {
        setPreviewLoading(false);
        if (res && res.success) {
          setPreviewHtml(res.data || "");
          setPreviewOpen(true);
        } else {
          toast.error("Unable to load preview");
        }
      })
      .catch(function () {
        setPreviewLoading(false);
        toast.error("Unable to load preview");
      });
  }

  var formApprovedProps = {
    userId: userId,
    formState: "isSelfDecalrationFormComplete",
    nextURL: "AdminEPF",
    approvedComment: approvedComment,
    setApprovedComment: setApprovedComment,
  };
  var formReSubmitProps = {
    userId: userId,
    formState: "isSelfDecalrationFormComplete",
    nextURL: "AdminEPF",
    reSubmitComment: reSubmitComment,
    setReSubmitComment: setReSubmitComment,
  };

  var fieldsReadOnly =
    isAdmin || isJoineeEmployeeFormLocked(sdfFlagButton);
  var showCandidateActions =
    !isAdmin && !isJoineeEmployeeFormLocked(sdfFlagButton);

  if (loading || !initialValues) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            color: JOINEE_FORM_COLORS.placeholder,
          }}
        >
          Loading Self Declaration…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      {isAdmin && !props.hideHrActions && canShowHrReviewActions(sdfFlagButton) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mb: 1.5,
            width: { xs: "100%", md: "82%" },
            maxWidth: 780,
            mx: "auto",
          }}
        >
          {canShowHrApprove(sdfFlagButton) ? (
            <ApprovedCustomizedDialogs formApprovedProps={formApprovedProps} />
          ) : null}
          {canShowHrResubmit(sdfFlagButton) ? (
            <ReSubmitCustomizedDialogs formReSubmitProps={formReSubmitProps} />
          ) : null}
        </Box>
      ) : null}

      <Formik
        key={formKey}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={buildValidationSchema()}
        onSubmit={handleSaveAndNext}
      >
        {function (formik) {
          var values = formik.values;
          var jobs = values.employments || [];
          var refs = values.references || [];
          var company =
            values.joiningCompanyName || "Spanidea Digital Pvt. Ltd.";

          return (
            <Form noValidate>
              <JoineeFormShell
                banner={
                  !isAdmin && sdfFlagButton === "resubmission required" ? (
                    <HrResubmitNotice comment={reSubmitComment} embedded />
                  ) : null
                }
                hideSteps
                onBack={
                  !isAdmin
                    ? function () {
                        navigate("/");
                      }
                    : null
                }
                title="Self Declaration Form"
                subtitle="Verify your personal and employment details, then complete the self-declaration to finalize your onboarding."
                headerAction={
                  isAdmin ? (
                    previewLoading ? (
                      <LoadingButtons />
                    ) : (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={openPreview}
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 400,
                          textTransform: "none",
                          color: JOINEE_FORM_COLORS.primary,
                          borderColor: JOINEE_FORM_COLORS.primary,
                          borderRadius: "6px",
                          minHeight: 32,
                          px: "12px",
                          bgcolor: "#FDFDFD",
                        }}
                      >
                        Preview Form
                      </Button>
                    )
                  ) : null
                }
                footer={
                  showCandidateActions ? (
                    <JoineeFormFooterActions
                      showPrevious
                      onPrevious={function () {
                        navigate("/");
                      }}
                      onDraft={function () {
                        return handleSaveDraft(formik);
                      }}
                      onNext={function () {
                        formik.validateForm().then(function (errors) {
                          if (errors && Object.keys(errors).length) {
                            notifyFormInvalid(errors);
                            formik.setTouched(
                              {
                                place: true,
                                date: true,
                                sdfLegalConsent: true,
                              },
                              true
                            );
                            setTimeout(function () {
                              var target = document.querySelector(
                                '.Mui-error, [aria-invalid="true"], [data-ack-error="true"]'
                              );
                              if (
                                target &&
                                typeof target.scrollIntoView === "function"
                              ) {
                                target.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }
                            }, 50);
                            return;
                          }
                          formik.submitForm();
                        });
                      }}
                      disabled={Boolean(busy)}
                      drafting={busy === "draft"}
                      saving={busy === "next"}
                      nextLabel="Save and next"
                    />
                  ) : isAdmin && !props.hideHrActions ? (
                    <JoineeFormFooterActions
                      showPrevious
                      onPrevious={function () {
                        navigate("/AdminEmployees");
                      }}
                      hideDraft
                      hideNext
                    />
                  ) : null
                }
              >
                <FormSectionTitle>Candidate Details</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Full Name:",
                      value: displayValue(values.candidate_details.name),
                    },
                    {
                      label: "Father/Husband Name:",
                      value: displayValue(
                        values.candidate_details.fatherName
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date of Birth:",
                      value: formatDob(values.candidate_details.dob),
                    },
                    {
                      label: "Phone Number:",
                      value: displayValue(
                        values.candidate_details.phoneNumber
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Marital Status:",
                      value: displayValue(values.maritalStatus),
                    },
                    {
                      label: "Gender:",
                      value: displayValue(values.gender),
                    },
                  ]}
                />

                <FormSectionTitle>Current Address</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Address Line 1:",
                      value: displayValue(
                        values.currentAddress.addressLine1
                      ),
                    },
                    {
                      label: "Address Line 2:",
                      value: displayValue(
                        values.currentAddress.addressLine2
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Landmark:",
                      value: displayValue(values.currentAddress.landmark),
                    },
                    {
                      label: "Postal Code:",
                      value: displayValue(
                        values.currentAddress.postalCode
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Period of Stay:",
                      value: formatPeriodOfStay(
                        values.currentAddress.periodOfStay
                      ),
                    },
                    {
                      label: "Nature of Residency:",
                      value: displayValue(
                        values.currentAddress.natureOfResidency
                      ),
                    },
                  ]}
                />

                <FormSectionTitle>Permanent Address</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Address Line 1:",
                      value: displayValue(
                        values.permanentAddress.addressLine1
                      ),
                    },
                    {
                      label: "Address Line 2:",
                      value: displayValue(
                        values.permanentAddress.addressLine2
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Landmark:",
                      value: displayValue(values.permanentAddress.landmark),
                    },
                    {
                      label: "Postal Code:",
                      value: displayValue(
                        values.permanentAddress.postalCode
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Period of Stay:",
                      value: formatPeriodOfStay(
                        values.permanentAddress.periodOfStay
                      ),
                    },
                    {
                      label: "Nature of Residency:",
                      value: displayValue(
                        values.permanentAddress.natureOfResidency
                      ),
                    },
                  ]}
                />

                <FormSectionTitle>Reference</FormSectionTitle>
                {refs.map(function (ref, index) {
                  return (
                    <ReadOnlyRow
                      key={"ref-" + index}
                      items={[
                        {
                          label: "Full Name:",
                          value: displayValue(ref.name),
                        },
                        {
                          label: "Phone Number:",
                          value: displayValue(ref.phone),
                        },
                      ]}
                    />
                  );
                })}

                <FormSectionTitle>College Details</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "College Name:",
                      value: displayValue(values.college_details.name),
                    },
                    {
                      label: "Registration Number:",
                      value: displayValue(
                        values.college_details.registrationNumber
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "University Name:",
                      value: displayValue(
                        values.college_details.UniversityName
                      ),
                    },
                    {
                      label: "Course Duration:",
                      value: displayValue(
                        values.college_details.CourseDuration
                      ),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Year of Passing:",
                      value: displayValue(
                        values.college_details.yearPassing
                      ),
                    },
                    {
                      label: "Result(in%):",
                      value: displayValue(values.college_details.result),
                    },
                  ]}
                />

                <FormSectionTitle>Employment Details</FormSectionTitle>
                {jobs.length ? (
                  jobs.map(function (job, index) {
                    return (
                      <Box key={"job-" + index} sx={{ mb: 2.5 }}>
                        {jobs.length > 1 ? (
                          <Typography
                            sx={{
                              fontFamily: JOINEE_FORM_FONT,
                              fontSize: 14,
                              fontWeight: 500,
                              color: "#171A1C",
                              mb: 1.5,
                            }}
                          >
                            Employer {index + 1}
                          </Typography>
                        ) : null}
                        <ReadOnlyRow
                          items={[
                            {
                              label: "Company Name:",
                              value: displayValue(job.companyName),
                            },
                            {
                              label: "Address:",
                              value: displayValue(job.address),
                            },
                          ]}
                        />
                        <ReadOnlyRow
                          items={[
                            {
                              label: "Employee ID:",
                              value: displayValue(job.companyID),
                            },
                            {
                              label: "Period of Employment:",
                              value: displayValue(job.period),
                            },
                          ]}
                        />
                        <ReadOnlyRow
                          items={[
                            {
                              label: "City:",
                              value: displayValue(job.cityCode),
                            },
                            {
                              label: "Designation:",
                              value: displayValue(job.designation),
                            },
                          ]}
                        />
                        <ReadOnlyRow
                          items={[
                            {
                              label: "State:",
                              value: displayValue(job.state),
                            },
                            {
                              label: "Remuneration:",
                              value: displayValue(job.remuneration),
                            },
                          ]}
                        />
                        <ReadOnlyRow
                          items={[
                            {
                              label: "Phone Number:",
                              value: displayValue(job.contact),
                            },
                            {
                              label: "Reported To:",
                              value: displayValue(job.reported),
                            },
                          ]}
                        />
                        <ReadOnlyRow
                          items={[
                            {
                              label: "Reported Person Designation:",
                              value: displayValue(job.reportedDesignation),
                            },
                            {
                              label: "Reason for Leaving:",
                              value: displayValue(job.reasonLeaving),
                            },
                          ]}
                        />
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      color: JOINEE_FORM_COLORS.placeholder,
                      mb: 2,
                    }}
                  >
                    {EMPTY_DISPLAY}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    alignSelf: "stretch",
                    width: "100%",
                    minHeight: 164,
                    py: "10px",
                    px: { xs: "12px", sm: "16px" },
                    gap: "10px",
                    bgcolor: "#F1F2F4",
                    borderRadius: "4px",
                    boxSizing: "border-box",
                    mb: 3,
                    mt: 1,
                  }}
                >
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      width: "100%",
                      pl: "20px",
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#2E3338",
                      lineHeight: "22px",
                      "& li": { mb: "6px" },
                      "& li:last-child": { mb: 0 },
                    }}
                  >
                    <li>
                      I hereby declare that I am an Employee of {company} and I
                      authorize all who may have information relevant to this
                      inquiry to disclose it to {company} and/or its
                      representative. I release all concerned from any
                      liability on account of such disclosures.
                    </li>
                    <li>
                      I also declare that the information provided by me in my
                      resume and application for employment to {company} and/or
                      its representative is authentic and I am liable for all
                      inaccuracies and omissions.
                    </li>
                    <li>
                      I promise to extend total co-operation and provide
                      relevant documents required.
                    </li>
                  </Box>
                </Box>

                <FormTwoCol>
                  <FormTextField
                    id="sdf-name"
                    name="name"
                    label="Full Name"
                    value={values.name}
                    disabled
                  />
                  <FormTextField
                    id="sdf-place"
                    rule="place"
                    name="place"
                    label="Place"
                    required
                    value={values.place}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "place")}
                    disabled={fieldsReadOnly}
                    inputProps={{ autoComplete: "off" }}
                  />
                </FormTwoCol>

                <FormTwoCol>
                  <FormDateField
                    id="sdf-date"
                    name="date"
                    label="Today’s Date"
                    placeholder="Select Date"
                    required
                    disableFuture
                    value={values.date}
                    onChange={function (iso) {
                      formik.setFieldValue("date", iso);
                    }}
                    onBlur={formik.handleBlur}
                    error={errMsg(formik, "date")}
                    disabled={fieldsReadOnly}
                  />
                  <Box />
                </FormTwoCol>

                {!isAdmin ? (
                  <Box
                    sx={{ mt: 1, mb: 1 }}
                    data-ack-error={
                      errMsg(formik, "sdfLegalConsent") ? "true" : undefined
                    }
                  >
                    <FormCheckboxField
                      name="sdfLegalConsent"
                      checked={Boolean(values.sdfLegalConsent)}
                      onChange={function (e) {
                        var checked = Boolean(e.target.checked);
                        formik.setFieldValue("sdfLegalConsent", checked, true);
                        formik.setFieldValue(
                          "sdfLegalConsentAt",
                          checked
                            ? values.sdfLegalConsentAt ||
                                new Date().toISOString()
                            : null,
                          false
                        );
                        formik.setFieldTouched(
                          "sdfLegalConsent",
                          true,
                          false
                        );
                        if (checked) {
                          formik.setFieldError("sdfLegalConsent", undefined);
                        }
                      }}
                      label="I confirm that the information provided is true and accurate, authorize the use of my previously submitted signature, and agree to all applicable terms and conditions."
                      disabled={fieldsReadOnly}
                      legal
                    />
                    {errMsg(formik, "sdfLegalConsent") &&
                    !values.sdfLegalConsent ? (
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 12,
                          fontWeight: 400,
                          color: JOINEE_FORM_COLORS.error,
                          mt: 0.5,
                          ml: "28px",
                        }}
                      >
                        {errMsg(formik, "sdfLegalConsent")}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </JoineeFormShell>
            </Form>
          );
        }}
      </Formik>

      <Dialog
        open={previewOpen}
        onClose={function () {
          setPreviewOpen(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 600,
            pr: 6,
          }}
        >
          Preview Form
          <IconButton
            aria-label="close"
            onClick={function () {
              setPreviewOpen(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Sdf;
